import { memberRole, presenceColor, type CallChatMessage, type MemberRole } from '@kanvas/board-core';
import {
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  type LocalAudioTrack,
  type LocalParticipant,
  type LocalVideoTrack,
  type Participant,
  type RemoteParticipant,
} from 'livekit-client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { useCallChat } from '../collab/hooks';
import { ApiError, api, type CallRecord } from '../lib/api';
import type { Identity } from '../lib/identity';
import { CallAudio, type AudioSink } from './CallAudio';

export type CameraFacing = 'user' | 'environment';

/** One tile in the call window — local or remote, video-capable or not. */
export interface CallParticipantView {
  identity: string;
  name: string;
  color: string;
  isLocal: boolean;
  micOn: boolean;
  camOn: boolean;
  speaking: boolean;
  cameraTrack: Track | null;
  /** Screen share, when this person is the one presenting. */
  screenShareTrack: Track | null;
  /** Mirror local front camera only — rear cameras look wrong flipped. */
  mirrorCamera: boolean;
  /** Teacher / curator / student, or null for members without one. */
  role: MemberRole | null;
}

export interface ScreenShareView {
  identity: string;
  name: string;
  track: Track;
  isLocal: boolean;
}

export type CallConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface CallContextValue {
  /** Whether the server even has a LiveKit deployment configured. */
  configured: boolean;
  /** A call already running on this board that this browser has not joined. */
  activeCall: CallRecord | null;
  /** People currently in that call, from LiveKit (0 while unknown). */
  activeCallParticipants: number;
  status: CallConnState;
  error: string | null;
  participants: CallParticipantView[];
  localMicOn: boolean;
  localCamOn: boolean;
  screenShare: ScreenShareView | null;
  isScreenSharing: boolean;
  /** Set when the browser refused to start playback without a user gesture. */
  audioBlocked: boolean;
  enableAudio: () => Promise<void>;
  join: () => Promise<void>;
  leave: () => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleCam: () => Promise<void>;
  /** Front ↔ rear on phones (and any device with more than one camera). */
  flipCamera: () => Promise<void>;
  /** True when flipping is worth offering — camera on and a second lens exists / mobile. */
  canFlipCamera: boolean;
  /** Which lens the local camera is using. */
  cameraFacing: CameraFacing;
  toggleScreenShare: () => Promise<void>;

  // ---- chat -----------------------------------------------------------------
  /** Whether the chat window is on screen. */
  chatOpen: boolean;
  toggleChat: () => void;
  closeChat: () => void;
  messages: CallChatMessage[];
  sendChat: (payload: { text?: string; imageUrl?: string | null; imageMime?: string | null }) => void;
  reactToChat: (messageId: string, emoji: string) => void;
  /** False for view-only links, which may read the chat but not write to it. */
  canChat: boolean;
  /** Teacher / curator / owner — can mute chat, mute call, kick. */
  canModerate: boolean;
  /** This browser is currently banned from the call chat. */
  chatMuted: boolean;
  /** When the chat mute ends, or null if indefinite / not muted. */
  chatMutedUntil: number | null;
  muteChat: (userId: string, durationMinutes?: number | null) => Promise<void>;
  unmuteChat: (userId: string) => Promise<void>;
  /** Whether a given participant is currently chat-muted. */
  isUserChatMuted: (userId: string) => boolean;
  muteCallParticipant: (userId: string, muted?: boolean) => Promise<void>;
  kickCallParticipant: (userId: string) => Promise<void>;
  /** Indefinite chat mute + kick from the call. */
  banFromCall: (userId: string) => Promise<void>;
  /** Local user id — for reaction “mine” highlights and self-moderation guards. */
  myUserId: string;
  /** Messages that arrived while the chat was closed. */
  unreadChat: number;
}

const CallContext = createContext<CallContextValue | null>(null);

export function useCall(): CallContextValue {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be used inside <CallProvider>');
  return ctx;
}

/** How often to check whether someone else started a call, while not on one. */
const ACTIVE_POLL_MS = 5000;

/**
 * A failed join is almost always one of three things, and the difference
 * matters: nothing to connect to, no permission, or signalling worked but
 * media never did — which is what a blocked UDP port looks like from here.
 */
function joinErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 503) return 'Звонки ещё не настроены на этом сервере.';
  if (e instanceof ApiError && e.code === 'banned_from_call') {
    const until = (e.body as { until?: number | null } | undefined)?.until ?? null;
    const when = until
      ? ` до ${new Date(until).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
      : '';
    return `Модератор закрыл вам доступ к этому звонку${when}.`;
  }
  const text = e instanceof Error ? e.message : '';
  if (/pc connection|ice|timeout/i.test(text)) {
    return 'Медиасоединение не установилось. Проверьте, что LiveKit доступен по UDP.';
  }
  if (/permission|denied|notallowed/i.test(text)) return 'Браузер не дал доступ к микрофону.';
  return 'Не удалось подключиться к звонку.';
}

/** Why a camera or microphone could not be switched on, in words a user can act on. */
function mediaErrorMessage(e: unknown, device: 'mic' | 'cam'): string {
  const name = e instanceof Error ? e.name : '';
  const text = e instanceof Error ? e.message : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || /permission|denied|notallowed/i.test(text)) {
    return device === 'cam' ? 'Браузер не дал доступ к камере.' : 'Браузер не дал доступ к микрофону.';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return device === 'cam' ? 'Камера занята другим приложением.' : 'Микрофон занят другим приложением.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return device === 'cam' ? 'Камера не найдена.' : 'Микрофон не найден.';
  }
  return device === 'cam' ? 'Не удалось включить камеру.' : 'Не удалось включить микрофон.';
}

/** Shown when a moderator has the microphone off. */
const MIC_MUTED_MESSAGE = 'Модератор выключил вам микрофон.';

function moderationErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'participant_not_in_call') return 'Участник уже вышел из звонка.';
    if (e.code === 'cannot_ban_moderator') return 'Нельзя закрыть звонок другому модератору.';
    if (e.status === 403) return 'Недостаточно прав для этого действия.';
    if (e.status === 503) return 'Звонки ещё не настроены на этом сервере.';
  }
  return 'Не удалось выполнить действие. Попробуйте ещё раз.';
}

/** How long a non-fatal notice (denied camera, failed moderation) stays up. */
const NOTICE_MS = 7000;

function participantColor(doc: BoardDoc, identity: Identity, p: Participant): string {
  if (p.identity === identity.userId) return identity.color;
  return doc.getPresence().find((peer) => peer.userId === p.identity)?.color ?? presenceColor(p.identity);
}

/**
 * The role comes out of the LiveKit participant metadata, which the server
 * signs into the token. Reading it here rather than asking the API per
 * participant means a late joiner's badge appears with them, and nobody can
 * award themselves one.
 */
function participantRole(p: Participant): MemberRole | null {
  if (!p.metadata) return null;
  try {
    const parsed = JSON.parse(p.metadata) as { roleSlug?: unknown };
    return typeof parsed.roleSlug === 'string' ? memberRole(parsed.roleSlug) : null;
  } catch {
    return null;
  }
}

function toView(
  doc: BoardDoc,
  identity: Identity,
  p: Participant,
  cameraFacing: CameraFacing,
): CallParticipantView {
  const isLocal = p.identity === identity.userId;
  return {
    identity: p.identity,
    name: p.name || p.identity,
    color: participantColor(doc, identity, p),
    isLocal,
    micOn: p.isMicrophoneEnabled,
    camOn: p.isCameraEnabled,
    speaking: p.isSpeaking,
    cameraTrack: p.getTrackPublication(Track.Source.Camera)?.track ?? null,
    screenShareTrack: p.getTrackPublication(Track.Source.ScreenShare)?.track ?? null,
    mirrorCamera: isLocal && cameraFacing === 'user',
    role: participantRole(p),
  };
}

/** Phones advertise one "camera" until permission; facingMode still switches lenses. */
function deviceLooksMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(pointer: coarse)').matches ||
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
  );
}

/**
 * Someone else's screen wins over your own preview: your own is a
 * confirmation, theirs is the thing being talked about.
 */
function findScreenShare(room: Room): ScreenShareView | null {
  const ordered: Participant[] = [...room.remoteParticipants.values(), room.localParticipant];
  for (const p of ordered) {
    const pub = p.getTrackPublication(Track.Source.ScreenShare);
    if (pub?.track) {
      return {
        identity: p.identity,
        name: p.name || p.identity,
        track: pub.track,
        isLocal: p === room.localParticipant,
      };
    }
  }
  return null;
}

/** Another participant already publishing a screen — only one share at a time. */
function findRemoteScreenSharer(room: Room): Participant | null {
  for (const p of room.remoteParticipants.values()) {
    if (p.getTrackPublication(Track.Source.ScreenShare)?.track) return p;
  }
  return null;
}

/** Every remote audio publication — microphones and shared-tab audio alike. */
function collectAudio(room: Room): AudioSink[] {
  const sinks: AudioSink[] = [];
  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.trackPublications.values()) {
      if (pub.kind === Track.Kind.Audio && pub.track) {
        sinks.push({ id: pub.trackSid, track: pub.track });
      }
    }
  }
  return sinks;
}

export function CallProvider({
  doc,
  identity,
  children,
}: {
  doc: BoardDoc;
  identity: Identity;
  children: ReactNode;
}) {
  const roomRef = useRef<Room | null>(null);
  /** Whether a moderator has this participant's microphone off (from the token). */
  const micMutedRef = useRef(false);
  // Bumped by every join and every leave: a join that finds the number moved
  // on after an await has been cancelled (or superseded) and must unwind.
  const joinGenRef = useRef(0);
  const joiningRef = useRef(false);
  const [status, setStatus] = useState<CallConnState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<CallParticipantView[]>([]);
  const [screenShare, setScreenShare] = useState<ScreenShareView | null>(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [audioSinks, setAudioSinks] = useState<AudioSink[]>([]);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [activeCall, setActiveCall] = useState<CallRecord | null>(null);
  const [activeCallParticipants, setActiveCallParticipants] = useState(0);
  const [callId, setCallId] = useState<string | null>(null);
  const [myRoleSlug, setMyRoleSlug] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [readAt, setReadAt] = useState(() => Date.now());
  const [canModerate, setCanModerate] = useState(false);
  const [chatMuted, setChatMuted] = useState(false);
  const [chatMutedUntil, setChatMutedUntil] = useState<number | null>(null);
  const [mutedChatIds, setMutedChatIds] = useState<Set<string>>(() => new Set());
  const [cameraFacing, setCameraFacing] = useState<CameraFacing>('user');
  const [canFlipCamera, setCanFlipCamera] = useState(false);
  const cameraFacingRef = useRef<CameraFacing>('user');
  cameraFacingRef.current = cameraFacing;

  // Chat lives in the document, so it arrives the same way nodes do — and
  // survives leaving and rejoining the call.
  const messages = useCallChat(doc, callId);

  const refresh = useCallback(
    (room: Room) => {
      const all: Participant[] = [room.localParticipant, ...room.remoteParticipants.values()];
      setParticipants(all.map((p) => toView(doc, identity, p, cameraFacingRef.current)));
      setScreenShare(findScreenShare(room));
      setAudioSinks(collectAudio(room));

      const local = room.localParticipant;
      // Read straight off the participant rather than inferring it from the
      // visible share: the sharer needs a working "stop" button even while
      // someone else's screen is the one on show.
      setIsScreenSharing(local.isScreenShareEnabled);
      doc.setPresence({
        micOn: local.isMicrophoneEnabled,
        camOn: local.isCameraEnabled,
        speaking: local.isSpeaking,
        inCall: true,
      });
    },
    [doc, identity],
  );

  // Polls "is anyone already on a call" while this browser is not connected,
  // so a board member who opens the board mid-call sees the incoming banner
  // instead of discovering it only once they happen to press "call".
  useEffect(() => {
    if (status !== 'idle') {
      setActiveCall(null);
      setActiveCallParticipants(0);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const res = await api.callStatus(doc.boardId);
        if (cancelled) return;
        setConfigured(res.configured);
        setActiveCall(res.call);
        setActiveCallParticipants(res.participantCount ?? 0);
        setCanModerate(res.canModerate);
        setChatMuted(res.chatMute !== null);
        setChatMutedUntil(res.chatMute?.until ?? null);
        setMutedChatIds(new Set(res.chatMutes.map((m) => m.userId)));
      } catch {
        // Transient network hiccup — the next tick tries again.
      }
    };
    void check();
    const timer = window.setInterval(check, ACTIVE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [doc.boardId, status]);

  // While on a call, refresh mute state so a chat ban applied by a moderator
  // reaches the target without a reload.
  useEffect(() => {
    if (status !== 'connected' && status !== 'reconnecting') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.callStatus(doc.boardId);
        if (cancelled) return;
        setCanModerate(res.canModerate);
        setChatMuted(res.chatMute !== null);
        setChatMutedUntil(res.chatMute?.until ?? null);
        setMutedChatIds(new Set(res.chatMutes.map((m) => m.userId)));
      } catch {
        // Next tick retries.
      }
    };
    void tick();
    const timer = window.setInterval(tick, ACTIVE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [doc.boardId, status]);

  const join = useCallback(async () => {
    // `roomRef` stays null through the first awaits, so it alone would let a
    // double click open two connections.
    if (roomRef.current || joiningRef.current) return;
    const gen = ++joinGenRef.current;
    joiningRef.current = true;
    const cancelled = () => joinGenRef.current !== gen;
    setStatus('connecting');
    setError(null);
    let started: CallRecord | null = null;
    let micTrack: LocalAudioTrack | null = null;
    let micError: unknown = null;
    let room: Room | null = null;
    let lostWhileJoining = false;

    // Undo whatever this attempt got as far as creating. Used both when it
    // failed and when the user hung up while it was still connecting.
    const unwind = async () => {
      if (room) {
        if (roomRef.current === room) roomRef.current = null;
        void room.disconnect();
      }
      // Hand the microphone back, or the browser keeps showing this tab as
      // recording after a call that never happened.
      micTrack?.stop();
      // A join that never connected must not leave a call marked as running:
      // that record is what shows everyone else an incoming-call banner for a
      // call nobody is actually on. Only the attempt that opened it clears it,
      // and not from under a newer attempt that is already on its way in.
      const superseded = cancelled() && (joiningRef.current || roomRef.current !== null);
      if (
        started &&
        started.startedBy === identity.userId &&
        Date.now() - started.startedAt < 15_000 &&
        !superseded
      ) {
        await api.stopCall(doc.boardId).catch(() => undefined);
      }
    };

    try {
      started = await api.startCall(doc.boardId);
      if (cancelled()) return void (await unwind());
      setCallId(started.id);
      const {
        token,
        url,
        canPublish,
        roleSlug,
        chatMuted: muted,
        micMuted = false,
        canModerate: moderate,
      } = await api.callToken(doc.boardId);
      micMutedRef.current = micMuted;
      if (cancelled()) return void (await unwind());
      setMyRoleSlug(roleSlug);
      setChatMuted(muted);
      setCanModerate(moderate);
      if (muted) {
        const status = await api.callStatus(doc.boardId).catch(() => null);
        if (cancelled()) return void (await unwind());
        setChatMutedUntil(status?.chatMute?.until ?? null);
        if (status) setMutedChatIds(new Set(status.chatMutes.map((m) => m.userId)));
      }

      const thisRoom = new Room({ adaptiveStream: true, dynacast: true });
      room = thisRoom;
      roomRef.current = thisRoom;

      // The microphone is taken *before* connecting, and not only to save a
      // round trip. Until an origin holds a microphone grant, browsers hide
      // host ICE candidates behind mDNS `.local` names — and Firefox then
      // offers the default route only, which on a machine with a VPN is the
      // tunnel rather than the LAN the media server is published on. Granted,
      // it publishes a real candidate per interface and ICE finds the one
      // that works instead of timing out after fifteen seconds.
      if (canPublish && !micMuted) {
        // Voice first, camera opt-in: a study call is mostly discussion, and
        // an unprompted camera is the surprise people mind most.
        micTrack = await createLocalAudioTrack().catch((e: unknown) => {
          micError = e;
          return null;
        });
        if (cancelled()) return void (await unwind());
      }

      const onChange = () => refresh(thisRoom);
      thisRoom.on(RoomEvent.ParticipantConnected, onChange);
      thisRoom.on(RoomEvent.ParticipantDisconnected, onChange);
      thisRoom.on(RoomEvent.TrackSubscribed, onChange);
      thisRoom.on(RoomEvent.TrackUnsubscribed, onChange);
      thisRoom.on(RoomEvent.TrackMuted, onChange);
      thisRoom.on(RoomEvent.TrackUnmuted, onChange);
      thisRoom.on(RoomEvent.LocalTrackPublished, onChange);
      thisRoom.on(RoomEvent.LocalTrackUnpublished, onChange);
      thisRoom.on(RoomEvent.ActiveSpeakersChanged, onChange);
      // Role badges live in participant metadata, so a change to it is a
      // change to the tiles.
      thisRoom.on(RoomEvent.ParticipantMetadataChanged, onChange);
      thisRoom.on(RoomEvent.AudioPlaybackStatusChanged, () => setAudioBlocked(!thisRoom.canPlaybackAudio));
      // A call that fails to connect fails in the media path far more often
      // than in ours, and the state sequence is the only thing that tells the
      // two apart from outside the browser.
      if (import.meta.env.DEV) {
        thisRoom.on(RoomEvent.ConnectionStateChanged, (state) => console.info('[call] state:', state));
        thisRoom.on(RoomEvent.MediaDevicesError, (e) => console.warn('[call] media device error:', e));
      }
      thisRoom.on(RoomEvent.Reconnecting, () => setStatus('reconnecting'));
      thisRoom.on(RoomEvent.Reconnected, () => setStatus('connected'));
      thisRoom.on(RoomEvent.Disconnected, (reason) => {
        // `leave` and a cancelled join let go of the room before disconnecting
        // it and tidy up themselves; a stale room must not reset a newer call.
        if (roomRef.current !== thisRoom) return;
        // Dropped before the join finished: that is a failed join, and the
        // join itself reports it (with a better message than "lost").
        if (joiningRef.current && !cancelled()) {
          lostWhileJoining = true;
          return;
        }
        roomRef.current = null;
        joinGenRef.current++;
        joiningRef.current = false;
        // Being removed or dropped is not the same as hanging up, and must not
        // look like it: say why the window is going away.
        if (reason === undefined || reason === DisconnectReason.CLIENT_INITIATED) {
          setStatus('idle');
        } else {
          setStatus('error');
          setError(
            reason === DisconnectReason.PARTICIPANT_REMOVED
              ? 'Модератор исключил вас из звонка.'
              : 'Соединение со звонком потеряно.',
          );
        }
        setParticipants([]);
        setScreenShare(null);
        setIsScreenSharing(false);
        setAudioSinks([]);
        setAudioBlocked(false);
        setChatOpen(false);
        setCanFlipCamera(false);
        cameraFacingRef.current = 'user';
        setCameraFacing('user');
        doc.setPresence({ micOn: false, camOn: false, speaking: false, inCall: false });
      });

      const assertStillIn = () => {
        if (lostWhileJoining) throw new Error('disconnected while joining');
      };

      await thisRoom.connect(url, token);
      if (cancelled()) return void (await unwind());
      assertStillIn();

      if (micTrack) {
        await thisRoom.localParticipant.publishTrack(micTrack).catch(() => undefined);
      } else if (canPublish && !micMuted) {
        // Permission was refused or no input exists; the call still works as
        // listen-only, and the mic button can try again later.
        await thisRoom.localParticipant
          .setMicrophoneEnabled(true)
          .then(() => {
            micError = null;
          })
          .catch((e: unknown) => {
            micError = e;
          });
      }
      if (cancelled()) return void (await unwind());
      assertStillIn();

      // Joining is a click, so this is the one moment autoplay policies are
      // guaranteed to allow playback to start.
      await thisRoom.startAudio().catch(() => undefined);
      if (cancelled()) return void (await unwind());
      assertStillIn();
      setAudioBlocked(!thisRoom.canPlaybackAudio);

      joiningRef.current = false;
      setStatus('connected');
      // Listen-only is fine, but not silently: say why the mic is off.
      if (micMuted) setError(MIC_MUTED_MESSAGE);
      else if (micError) setError(mediaErrorMessage(micError, 'mic'));
      refresh(thisRoom);
    } catch (e) {
      // Hanging up mid-connect makes the pending awaits reject; that is the
      // user's doing, not a failure to report.
      if (cancelled()) return void (await unwind());
      if (import.meta.env.DEV) console.error('[call] join failed:', e);
      // Released before the (awaited) clean-up so "retry" is not swallowed.
      joinGenRef.current++;
      joiningRef.current = false;
      setStatus('error');
      setError(joinErrorMessage(e));
      await unwind();
    }
  }, [doc, identity.userId, refresh]);

  const leave = useCallback(async () => {
    const room = roomRef.current;
    roomRef.current = null;
    // Cancels a join still in flight: it notices at its next await and unwinds.
    joinGenRef.current++;
    joiningRef.current = false;
    setStatus('idle');
    setError(null);
    setParticipants([]);
    setScreenShare(null);
    setIsScreenSharing(false);
    setAudioSinks([]);
    setAudioBlocked(false);
    setChatOpen(false);
    setCanFlipCamera(false);
    cameraFacingRef.current = 'user';
    setCameraFacing('user');
    doc.setPresence({ micOn: false, camOn: false, speaking: false, inCall: false });

    // Also the way a failed attempt is dismissed, in which case there is no
    // room to disconnect and nothing on the server to wind down.
    if (!room) return;

    const wasAlone = room.remoteParticipants.size === 0;
    await room.disconnect();
    if (wasAlone) {
      try {
        await api.stopCall(doc.boardId);
      } catch {
        // Best-effort — the room will empty-timeout on the LiveKit side regardless.
      }
    }
  }, [doc]);

  const enableAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.startAudio();
    } catch {
      // Still blocked — the button stays up for another try.
    }
    setAudioBlocked(!room.canPlaybackAudio);
  }, []);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled);
      setError(null);
    } catch (e) {
      setError(micMutedRef.current ? MIC_MUTED_MESSAGE : mediaErrorMessage(e, 'mic'));
    }
    refresh(room);
  }, [refresh]);

  const toggleCam = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const enabling = !room.localParticipant.isCameraEnabled;
    if (enabling) {
      try {
        await room.localParticipant.setCameraEnabled(true, {
          facingMode: cameraFacingRef.current,
        });
        setError(null);
      } catch (e) {
        // Denied, busy or missing — say which, instead of a dead button.
        setError(mediaErrorMessage(e, 'cam'));
        refresh(room);
        return;
      }
      try {
        const devices = await Room.getLocalDevices('videoinput');
        setCanFlipCamera(devices.length > 1 || deviceLooksMobile());
      } catch {
        setCanFlipCamera(deviceLooksMobile());
      }
    } else {
      await room.localParticipant.setCameraEnabled(false).catch(() => undefined);
      setCanFlipCamera(false);
    }
    refresh(room);
  }, [refresh]);

  const flipCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room || !room.localParticipant.isCameraEnabled) return;
    const next: CameraFacing = cameraFacingRef.current === 'user' ? 'environment' : 'user';
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const videoTrack = pub?.track as LocalVideoTrack | undefined;
    try {
      if (videoTrack) {
        await videoTrack.restartTrack({ facingMode: next });
      } else {
        await room.localParticipant.setCameraEnabled(false);
        await room.localParticipant.setCameraEnabled(true, { facingMode: next });
      }
      cameraFacingRef.current = next;
      setCameraFacing(next);
    } catch {
      // The other lens is missing or the browser refused — keep the current one.
    }
    refresh(room);
  }, [refresh]);

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const enabling = !room.localParticipant.isScreenShareEnabled;
    // One share per call: starting a second one would burn bandwidth for a
    // track the UI does not even show.
    if (enabling) {
      // The button is disabled while someone presents, but their share can
      // start between the render and the click.
      const other = findRemoteScreenSharer(room);
      if (other) {
        setError(`Сейчас экран демонстрирует ${other.name || other.identity}.`);
        return;
      }
    }
    try {
      await room.localParticipant.setScreenShareEnabled(enabling, {
        audio: true,
      });
    } catch {
      // Dismissing the browser's own picker throws; nothing changed.
    }
    refresh(room);
  }, [refresh]);

  const sendChat = useCallback(
    (payload: { text?: string; imageUrl?: string | null; imageMime?: string | null }) => {
      if (!callId || chatMuted) return;
      const role = memberRole(myRoleSlug);
      doc.sendCallChat({
        callId,
        userId: identity.userId,
        name: identity.name,
        color: identity.color,
        roleSlug: role?.slug ?? null,
        roleLabel: role?.label ?? null,
        text: payload.text ?? '',
        imageUrl: payload.imageUrl ?? null,
        imageMime: payload.imageMime ?? null,
      });
    },
    [callId, chatMuted, doc, identity, myRoleSlug],
  );

  const reactToChat = useCallback(
    (messageId: string, emoji: string) => {
      if (!doc.canComment()) return;
      doc.toggleCallChatReaction(messageId, emoji, identity.userId);
    },
    [doc, identity.userId],
  );

  const muteChatRaw = useCallback(
    async (userId: string, durationMinutes?: number | null) => {
      const mute = await api.muteChat(doc.boardId, userId, durationMinutes);
      setMutedChatIds((ids) => new Set(ids).add(userId));
      if (userId === identity.userId) {
        setChatMuted(true);
        setChatMutedUntil(mute.until);
      }
    },
    [doc.boardId, identity.userId],
  );

  // Moderation is fired from menus that do not wait for an answer, so these
  // never reject: a failure is reported through `error` instead.
  const muteChat = useCallback(
    async (userId: string, durationMinutes?: number | null) => {
      try {
        await muteChatRaw(userId, durationMinutes);
      } catch (e) {
        setError(moderationErrorMessage(e));
      }
    },
    [muteChatRaw],
  );

  const unmuteChat = useCallback(
    async (userId: string) => {
      try {
        await api.unmuteChat(doc.boardId, userId);
      } catch (e) {
        setError(moderationErrorMessage(e));
        return;
      }
      setMutedChatIds((ids) => {
        const next = new Set(ids);
        next.delete(userId);
        return next;
      });
      if (userId === identity.userId) {
        setChatMuted(false);
        setChatMutedUntil(null);
      }
    },
    [doc.boardId, identity.userId],
  );

  const muteCallParticipant = useCallback(
    async (userId: string, muted = true) => {
      try {
        await api.muteCallParticipant(doc.boardId, userId, muted);
      } catch (e) {
        setError(moderationErrorMessage(e));
      }
    },
    [doc.boardId],
  );

  const kickCallParticipant = useCallback(
    async (userId: string) => {
      try {
        await api.kickCallParticipant(doc.boardId, userId);
      } catch (e) {
        setError(moderationErrorMessage(e));
      }
    },
    [doc.boardId],
  );

  // The server removes them from the call, keeps them out on rejoin and
  // closes the chat to them, all in one step.
  const banFromCall = useCallback(
    async (userId: string) => {
      try {
        await api.banFromCall(doc.boardId, userId, null);
      } catch (e) {
        setError(moderationErrorMessage(e));
        return;
      }
      setMutedChatIds((ids) => new Set(ids).add(userId));
    },
    [doc.boardId],
  );

  const toggleChat = useCallback(() => {
    setChatOpen((open) => {
      if (!open) setReadAt(Date.now());
      return !open;
    });
  }, []);

  const closeChat = useCallback(() => setChatOpen(false), []);

  // On a live call `error` is a notice, not a state — let it fade so an old
  // "camera is busy" does not sit under the controls for the rest of the call.
  useEffect(() => {
    if (!error || status !== 'connected') return;
    const timer = window.setTimeout(() => setError(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [error, status]);

  // An open chat is a read chat: keep the marker moving so closing it does
  // not immediately show a badge for messages already on screen.
  useEffect(() => {
    if (chatOpen) setReadAt(Date.now());
  }, [chatOpen, messages.length]);

  const unreadChat = chatOpen
    ? 0
    : messages.filter((m) => m.ts > readAt && m.userId !== identity.userId).length;

  // Leaving the tab should not leave a phantom "in call" presence behind.
  useEffect(() => {
    const onUnload = () => {
      void roomRef.current?.disconnect();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      void roomRef.current?.disconnect();
    };
  }, []);

  const value = useMemo<CallContextValue>(
    () => ({
      configured,
      activeCall,
      activeCallParticipants,
      status,
      error,
      participants,
      localMicOn: participants.find((p) => p.isLocal)?.micOn ?? false,
      localCamOn: participants.find((p) => p.isLocal)?.camOn ?? false,
      screenShare,
      isScreenSharing,
      audioBlocked,
      enableAudio,
      join,
      leave,
      toggleMic,
      toggleCam,
      flipCamera,
      canFlipCamera,
      cameraFacing,
      toggleScreenShare,
      chatOpen,
      toggleChat,
      closeChat,
      messages,
      sendChat,
      reactToChat,
      canChat: doc.canComment() && !chatMuted,
      canModerate,
      chatMuted,
      chatMutedUntil,
      muteChat,
      unmuteChat,
      isUserChatMuted: (userId: string) => mutedChatIds.has(userId),
      muteCallParticipant,
      kickCallParticipant,
      banFromCall,
      myUserId: identity.userId,
      unreadChat,
    }),
    [
      configured,
      activeCall,
      activeCallParticipants,
      status,
      error,
      participants,
      screenShare,
      isScreenSharing,
      audioBlocked,
      enableAudio,
      join,
      leave,
      toggleMic,
      toggleCam,
      flipCamera,
      canFlipCamera,
      cameraFacing,
      toggleScreenShare,
      chatOpen,
      toggleChat,
      closeChat,
      messages,
      sendChat,
      reactToChat,
      chatMuted,
      canModerate,
      chatMutedUntil,
      muteChat,
      unmuteChat,
      muteCallParticipant,
      kickCallParticipant,
      banFromCall,
      unreadChat,
      doc,
      mutedChatIds,
      identity.userId,
    ],
  );

  return (
    <CallContext.Provider value={value}>
      <CallAudio sinks={audioSinks} />
      {children}
    </CallContext.Provider>
  );
}

export type { RemoteParticipant, LocalParticipant };
